import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { extendTheme, ChakraProvider } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout from './components/AdminComponents/MainLayout';
import UserHeader from './components/UserComponents/UserHeader';
import ErrorBoundary from './utils/errorBoundary';
import { useLazyGetMeQuery } from './slices/apiSlice';
import { setCredentials, logout } from './slices/authSlice';
import Loader from "./components/Loader";
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';

// Theme configuration
const config = {
  initialColorMode: 'system',
  useSystemColorMode: true,
}

const colors = {
  custom: {
    900: '#fafafa',
    800: '#153e75',
    700: '#2a69ac',
  },
}

const theme = extendTheme({ colors, config });

export default function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  const { adminInfo } = useSelector((state) => state.adminAuth);
  const isUserPage = !isAdminPage;
  const { userInfo } = useSelector((state) => state.auth);
  const [getGoogleUser, { isLoading }] = useLazyGetMeQuery();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem("i18nextLng") || "en";
    if (i18n.language !== savedLang) {
      i18n.changeLanguage(savedLang);
    }
    document.documentElement.dir = savedLang === "mm" ? "ltr" : "ltr";
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await getGoogleUser().unwrap();
        if (response.success) {
          dispatch(setCredentials(response.user));
        } else {
          dispatch(logout());
        }
      } catch (error) {
        dispatch(logout());
      } finally {
        setLoading(false);
      }
    };

    if (!userInfo && !isLoading) {
      fetchUser();
    }
  }, [userInfo, getGoogleUser, dispatch, isLoading]);

  if (loading || isLoading) {
    return (
      <ChakraProvider theme={theme}>
        <Loader />
      </ChakraProvider>
    );
  }

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ChakraProvider theme={theme}>
            {isAdminPage && adminInfo ? (
              <MainLayout>
                <Outlet />
              </MainLayout>
            ) : isUserPage && userInfo ? (
              <>
                <Outlet />
                <UserHeader />
              </>
            ) : (
              <Outlet />
            )}
        </ChakraProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

// import { useEffect, useState } from "react";
// import { Outlet, useLocation } from "react-router-dom";
// import { extendTheme, ChakraProvider, Box, useColorModeValue, CSSReset, GlobalStyle } from '@chakra-ui/react';
// import { useSelector, useDispatch } from 'react-redux';
// import MainLayout from './components/AdminComponents/MainLayout';
// import UserHeader from './components/UserComponents/UserHeader';
// import ErrorBoundary from './utils/errorBoundary';
// import { useLazyGetMeQuery } from './slices/apiSlice';
// import { setCredentials, logout } from './slices/authSlice';
// import Loader from "./components/Loader";
// import { I18nextProvider } from 'react-i18next';
// import i18n from './i18n';

// // 2. Extend the theme to include custom colors, fonts, etc
// const colors = {
//   custom: {
//     900: '#fafafa',
//     800: '#153e75',
//     700: '#2a69ac',
//   },
//   light: {
//     bg: '#0d9488', // Your light mode color
//   },
//   dark: {
//     bg: '#1A202C', // Your dark mode color
//   },
//   config: {
//     initialColorMode: 'system', // Important for auto-detection
//     useSystemColorMode: true,
//   }
// }

// const theme = extendTheme({ colors })

// export default function App() {
//   const dispatch = useDispatch();
//   const location = useLocation();
//   const isAdminPage = location.pathname.startsWith("/admin");
//   const { adminInfo } = useSelector((state) => state.adminAuth);
//   const isUserPage = location.pathname.startsWith("/");
//   const { userInfo } = useSelector((state) => state.auth);
//   const bgColorDark = useColorModeValue('teal.600', 'gray.800');
//   const [getGoogleUser, { isLoading }] = useLazyGetMeQuery();

//   // Add loading state
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     const savedLang = localStorage.getItem("i18nextLng") || "en";
//     if (i18n.language !== savedLang) {
//       i18n.changeLanguage(savedLang);
//     }
//     document.documentElement.dir = savedLang === "mm" && "ltr";
//   }, []);

//   useEffect(() => {
//     const fetchUser = async () => {
//       setLoading(true); // Start loading
//       try {
//         const response = await getGoogleUser().unwrap();

//         //console.log("API Response:", response); // Log full response
//         if (response.success) {
//           dispatch(setCredentials(response.user)); // Store user in Redux
//         } else {
//           //console.warn("User fetch failed:", response);
//           dispatch(logout());
//         }
//       } catch (error) {
//         //console.error("Error fetching user:", error);
//         dispatch(logout());
//       } finally {
//         setLoading(false); // Stop loading
//       }
//     };

//     // Fetch user only if not already in Redux state
//     if (!userInfo && !isLoading) {
//       fetchUser();
//     }
//   }, [userInfo, getGoogleUser, dispatch, isLoading]);

//   // 1. Create a custom global style component
// const GlobalStyles = () => (
//   <>
//     <CSSReset />
//     <GlobalStyle
//       styles={{
//         html: {
//           backgroundColor: 'var(--chakra-colors-dark-bg)', // Default dark
//         },
//         body: {
//           backgroundColor: 'var(--chakra-colors-dark-bg)', // Default dark
//           transition: 'background-color 0.2s',
//           minHeight: '100vh',
//         },
//         '@media (prefers-color-scheme: light)': {
//           html: {
//             backgroundColor: 'var(--chakra-colors-light-bg)',
//           },
//           body: {
//             backgroundColor: 'var(--chakra-colors-light-bg)',
//           },
//         },
//       }}
//     />
//   </>
// );

//   // Render loading spinner while fetching user info
//   if (loading || isLoading) {
//     return (
//       <ChakraProvider theme={theme}>
//         <Loader/>
//       </ChakraProvider>
//     );
//   }
  
//   return (
//     <ErrorBoundary>
//       <I18nextProvider i18n={i18n}>
//       <ChakraProvider theme={theme}>
//         <GlobalStyles />
        
//         {/* 3. Safe area container with proper styling */}
//         <Box
//           position="fixed"
//           top="0"
//           left="0"
//           right="0"
//           height="env(safe-area-inset-top)"
//           bg={bgColorDark}
//           zIndex="9999"
//         />
        
//         {/* 4. Main content wrapper */}
//         <Box
//           position="relative"
//           pt="env(safe-area-inset-top)"
//           minHeight="100vh"
//           bg={bgColorDark}
//         >
//           {isAdminPage && adminInfo ? (
//             <MainLayout>
//               <Outlet/>
//             </MainLayout>
//           ) : isUserPage && userInfo ? (
//             <>
//               <Outlet />
//               <UserHeader />
//             </>
//           ) : (
//             <Outlet />
//           )}
//         </Box>
//       </ChakraProvider>
//         {/* <ChakraProvider theme={theme}>
//           {
//             isAdminPage && adminInfo ? (
//               <MainLayout>
//                 <Outlet/>
//               </MainLayout>
//             ) : isUserPage && userInfo ? (
//               <Box 
//                 bg={bgColorDark} 
//                 pt="env(safe-area-inset-top)" 
//                 w="100%"
//                 >
//                 <Outlet />
//                 <UserHeader />
//               </Box>
            
//             ) : (
//               // Default rendering (for non-logged-in users or non-user pages)
//               <Outlet />
//             )
//           }
//         </ChakraProvider> */}
//       </I18nextProvider>
//     </ErrorBoundary>
//   );
// };

